/* eslint-disable max-lines-per-function */
/* eslint-disable no-extra-parens */
/* eslint-disable array-element-newline */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {action, makeObservable, observable} from 'mobx';
import {LitElement, html, nothing} from 'lit';
import {ringBuilderStore} from 'extensions/ring-builder/data/ring-builder-store';
import {DIAMOND_PREVIEW_MIN_CARAT, DIAMOND_PREVIEW_MAX_CARAT} from 'extensions/ring-builder/data/constants';
import {createRef, ref} from 'lit/directives/ref.js';
import {defineCustomElement, roundToIncrement} from 'extensions/utils';
import styles from './diamond-preview.component.scss?as=stylesheet';

class DiamondPreview extends MobxReactionUpdate(LitElement) {

	static get styles () {
		return styles;
	}

	// eslint-disable-next-line max-lines-per-function
	static get properties () {
		return {
			asscherMax: {
				attribute: 'asscher-max',
				type: String
			},
			asscherMin: {
				attribute: 'asscher-min',
				type: String
			},
			carat: {
				type: String
			},
			cushionMax: {
				attribute: 'cushion-max',
				type: String
			},
			cushionMin: {
				attribute: 'cushion-min',
				type: String
			},
			emeraldMax: {
				attribute: 'emerald-max',
				type: String
			},
			emeraldMin: {
				attribute: 'emerald-min',
				type: String
			},
			halo: {
				type: Boolean
			},
			heartMax: {
				attribute: 'heart-max',
				type: String
			},
			heartMin: {
				attribute: 'heart-min',
				type: String
			},
			marquiseMax: {
				attribute: 'marquise-max',
				type: String
			},
			marquiseMin: {
				attribute: 'marquise-min',
				type: String
			},
			ovalMax: {
				attribute: 'oval-max',
				type: String
			},
			ovalMin: {
				attribute: 'oval-min',
				type: String
			},
			pearMax: {
				attribute: 'pear-max',
				type: String
			},
			pearMin: {
				attribute: 'pear-min',
				type: String
			},
			princessMax: {
				attribute: 'princess-max',
				type: String
			},
			princessMin: {
				attribute: 'princess-min',
				type: String
			},
			productCode: {
				attribute: 'product-code',
				type: String
			},
			radiantMax: {
				attribute: 'radiant-max',
				type: String
			},
			radiantMin: {
				attribute: 'radiant-min',
				type: String
			},
			roundMax: {
				attribute: 'round-max',
				type: String
			},
			roundMin: {
				attribute: 'round-min',
				type: String
			},
			shape: {
				type: String
			},
			store: {
				state: true,
				type: Object
			}
		};
	}

	shapesRef = createRef();

	sliderRef = createRef();

	constructor () {
		super();

		this.defaultShapes = [
			'Round',
			'Cushion',
			'Radiant',
			'Princess',
			'Pear',
			'Oval',
			'Emerald',
			'Heart',
			'Asscher',
			'Marquise'
		];

		this.defaultHaloShapes = [
			'Round',
			'Oval'
		];

		this.ovalFirstSKUs = [
			'2SM000525',
			'2SM000526',
			'2SM000527',
			'2FSSW4130'
		];

		this.shapes = [];
		this.productCode = '';
		this.previousProductCode = '';

		// Make the 'shapes' object observable so we can dynamically generate a shapes array
		makeObservable(this, {
			setShapes: action,
			shapes: observable.deep
		});

		this.productImageSlider = document.getElementById('js-product-images');
	}

	setShapes (shapes) {
		this.shapes = shapes;
	}

	async firstUpdated () {
		// Assign store
		this.store = await ringBuilderStore;

		// Set the shapes array
		this.handlePropertyChange();
	}

	updated (changedProperties) {
		for (let [key, value] of changedProperties) {

			if (key?.indexOf('productCode') !== -1 && value) {
				this.previousProductCode = value;
			}

			if (key?.indexOf('Min') !== -1 || key?.indexOf('Max') !== -1 || key?.indexOf('productCode') !== -1) {
				this.handlePropertyChange();
				return;
			}
		}
	}

	getMinMaxFromShape (shape) {
		const shapeLowercase = shape.toLowerCase();
		const min = parseFloat(this[`${shapeLowercase}Min`]);
		const max = parseFloat(this[`${shapeLowercase}Max`]);

		return [min, max];
	}

	get min () {
		const shapeLowercase = String(this.shape).toLowerCase();
		return roundToIncrement(Math.max(DIAMOND_PREVIEW_MIN_CARAT, parseFloat(this[`${shapeLowercase}Min`]) || DIAMOND_PREVIEW_MIN_CARAT), 0.25, 'ceil');
	}

	get max () {
		const shapeLowercase = String(this.shape).toLowerCase();
		return roundToIncrement(Math.min(DIAMOND_PREVIEW_MAX_CARAT, parseFloat(this[`${shapeLowercase}Max`]) || DIAMOND_PREVIEW_MAX_CARAT), 0.25, 'ceil');
	}

	get isOvalFirstProduct () {
		return this.ovalFirstSKUs?.includes(this.productCode);
	}

	/**
	 * Sets `shapes`, `shape` and `carat` default values and updates preview on change
	 */
	async handlePropertyChange () {
		await this.updateComplete;

		let shapesRaw = this.halo ? this.defaultHaloShapes : this.defaultShapes;
		if (this.isOvalFirstProduct && shapesRaw) {
			const ovalIndex = shapesRaw.findIndex(shape => shape === 'Oval');
			if (ovalIndex > -1) {
				const ovalMember = shapesRaw[ovalIndex];
				shapesRaw.splice(ovalIndex, 1);
				shapesRaw.splice(0, 0, ovalMember);

				if (this.previousProductCode && !this.ovalFirstSKUs?.includes(this.previousProductCode)) {
					this.shape = shapesRaw[0];
				}
			}
		}

		const shapes = shapesRaw
			.map(defaultShape => {
				const [min, max] = this.getMinMaxFromShape(defaultShape);

				if (!min || !max) {
					return null;
				}

				return defaultShape;
			})
			.filter(shape => shape !== null);

		this.setShapes(shapes);

		// Reset shape property to default if the shape was not found
		const found = this.shapes.find(shape => this.shape === shape);
		if (!found || this.shape === void 0 || this.shape === null) {
			this.shape = this.shapes[0];
			this.carat = null;
		}

		// Set default carat
		if (this.carat === void 0 || this.carat === null) {
			if (this.min <= 1 && this.max > 1) {
				this.carat = 1.00;
			}
			else {
				this.carat = this.min + 0.25;
			}
		}

		// Update preview
		this.updatePreview({
			carat: this.carat,
			shape: this.shape
		});
	}

	handleChange () {
		this.shape = this.shapesRef?.value?.shape || this.shapes[0];

		this.updatePreview({
			carat: this.sliderRef?.value?.carat,
			shape: this.shapesRef?.value?.shape
		});
	}

	updatePreview (preview = {}) {
		// eslint-disable-next-line no-unused-expressions
		this.store?.diamond?.setPreview({
			...(this.store?.diamond?.preview || {}),
			...preview
		});

		// Update product slider index
		if (this.productImageSlider?.slick && this.productImageSlider?.setting_image_index > -1) {
			$(this.productImageSlider).slick('slickGoTo', this.productImageSlider.setting_image_index);
		}
	}

	render () {
		if (this.store?.diamond?.preview?.enabled === false) {
			// Add .hide class when there's no content
			if (!this.classList.contains('hide')) {
				this.classList.add('hide');
			}
			return nothing;
		}

		// Remove .hide class when there is content
		if (this.classList.contains('hide')) {
			this.classList.remove('hide');
		}

		return html`
		<div class="diamond-preview-container">
			<div class="diamond-preview-heading">
				<div class="diamond-preview-title">Try With Diamonds & Carat Sizes</div>
			</div>
			<rb-diamond-preview-shapes ${ref(this.shapesRef)} .shapes="${this.shapes}" shape="${this.shape}" @change="${this.handleChange}"></rb-diamond-preview-shapes>
			<rb-diamond-preview-carat-slider ${ref(this.sliderRef)} carat="${this.carat}" min="${this.min}" max="${this.max}" @change="${this.handleChange}"></rb-diamond-preview-carat-slider>
		</div>
		`;
	}
};

defineCustomElement('rb-diamond-preview', DiamondPreview);
